import React from 'react';
import { Box, Typography } from '@mui/material';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PillButton from './ui/atoms/PillButton';
import { useAppDispatch, useAppSelector } from '../redux/store';
import {
	PREVIEW_SESSION_KEY,
	clearPreviewMarker,
	fetchSuperchatConfig,
	fetchSuperchatRevision,
	previewEnded,
	previewStarted
} from '../redux/slices/superchat';
// One definition of where the widget is loaded from, shared with the parser that
// refuses a snippet pointing anywhere else.
import { SUPERCHAT_HOST } from '../pages/Admin/superchatSnippet';
import { dropVendorPreload } from './dropVendorPreload';

/**
 * Injects the Superchat Web Widget, or does not, according to configuration
 * Ops own.
 *
 * Mounted above the router rather than inside it: "before login" means /login,
 * /error and the MFA redirect, none of which mount a protected layout. A widget
 * rendered from inside the router would be missing from exactly the screens where
 * someone is most likely to want to ask for help.
 *
 * Three things have to agree before the script is added:
 *   - the superchat-widget-enabled flag is on
 *   - a configuration with an application key exists
 *   - the placement matches the current side of the login boundary
 *
 * The flag is the kill switch and is checked first, so turning it off removes the
 * widget without touching configuration.
 */

const PREVIEW_PARAM = 'superchatPreview';
/**
 * Our loader tag's id. It must not be `superchat-widget`.
 *
 * That is the id Superchat's own SDK looks for to decide whether a webchat is
 * already running:
 *
 *     if (document.getElementById(SUPERCHAT_NAMESPACE)) throw new Error(...)
 *
 * with SUPERCHAT_NAMESPACE === 'superchat-widget'. Naming our script tag that
 * meant the SDK found our tag, concluded it had already initialised, and threw —
 * so the launcher never rendered, on any page, however the widget was
 * configured. Everything else looked correct: the config loaded, both scripts
 * returned 200, and window.Superchat existed.
 */
export const SCRIPT_ID = 'pearsana-superchat-loader';

/** The id the vendor SDK claims. Ours must never equal it. */
export const VENDOR_NAMESPACE_ID = 'superchat-widget';

/**
 * The preview marker arrives in the URL once and then lives in sessionStorage, so
 * it survives navigation and the sign-in redirect without staying in the address
 * bar to be copied around or bookmarked.
 */
export const readPreviewVersion = (search: string, storage: Storage | undefined): number | null => {
	let raw: string | null = null;
	try {
		raw = new URLSearchParams(search).get(PREVIEW_PARAM);
	} catch {
		raw = null;
	}
	if (raw === null) {
		try {
			raw = storage?.getItem(PREVIEW_SESSION_KEY) ?? null;
		} catch {
			// Private windows and blocked storage throw; no preview is the right answer.
			return null;
		}
	}
	const version = Number(raw);
	return Number.isInteger(version) && version > 0 ? version : null;
};

/** Whether a placement allows the widget on this side of the login boundary. */
export const placementAllows = (placement: string | undefined, signedIn: boolean): boolean => {
	if (placement === 'always') return true;
	if (placement === 'pre-login') return !signedIn;
	if (placement === 'post-login') return signedIn;
	return false;
};

interface SuperchatWidgetProps {
	/** Whether there is an authenticated session. */
	signedIn: boolean;
}

const SuperchatWidget = ({ signedIn }: SuperchatWidgetProps) => {
	const dispatch = useAppDispatch();
	const { config, previewVersion, loaded } = useAppSelector((state) => state.superchat);
	const enabled = useAppSelector((state) => state.featureFlags.flags['superchat-widget-enabled'] === true);

	// Resolve the preview marker before asking for any configuration, so a preview
	// session never briefly loads the published widget first.
	React.useEffect(() => {
		const version = readPreviewVersion(window.location.search, window.sessionStorage);
		if (version === null) {
			dispatch(fetchSuperchatConfig());
			return;
		}
		try {
			window.sessionStorage.setItem(PREVIEW_SESSION_KEY, String(version));
		} catch {
			// Preview still works for this page; it just will not survive navigation.
		}
		// Take the marker out of the address bar so it is not copied or bookmarked.
		if (new URLSearchParams(window.location.search).has(PREVIEW_PARAM)) {
			const url = new URL(window.location.href);
			url.searchParams.delete(PREVIEW_PARAM);
			window.history.replaceState(null, '', url.toString());
		}
		dispatch(previewStarted(version));
		dispatch(fetchSuperchatRevision(version));
	}, [dispatch]);

	const shouldRender =
		loaded &&
		Boolean(config?.applicationKey) &&
		placementAllows(config?.placement, signedIn) &&
		// A preview deliberately ignores the kill switch: Ops need to see a widget
		// they have not turned on yet. Nobody else is affected — the marker is in
		// this browser's session only.
		(enabled || previewVersion !== null);

	const applicationKey = config?.applicationKey;

	React.useEffect(() => {
		const existing = document.getElementById(SCRIPT_ID);

		// Narrowed here rather than asserted below: shouldRender already requires a
		// key, and re-checking it is what lets the compiler agree.
		if (!shouldRender || !applicationKey) {
			// Superchat has no teardown API, so a full reload is the only honest way
			// to remove an injected widget. Rather than reload under the reader, the
			// script is left in place and simply never added again.
			return;
		}

		if (existing) {
			return;
		}

		const script = document.createElement('script');
		script.id = SCRIPT_ID;
		script.src = `https://${SUPERCHAT_HOST}/snippet.js?applicationKey=${encodeURIComponent(applicationKey)}`;
		script.referrerPolicy = 'no-referrer-when-downgrade';
		script.async = true;
		document.body.appendChild(script);

		// Started with the script it is about to answer for, and torn down with it.
		return dropVendorPreload();
	}, [shouldRender, applicationKey]);

	const exitPreview = () => {
		clearPreviewMarker();
		dispatch(previewEnded());
		window.location.reload();
	};

	if (previewVersion === null) {
		return null;
	}

	// Previewing has to be unmistakable, or someone reports "the widget is live"
	// on the strength of their own session. Pearsana red rather than a generic
	// warning amber: this is the app speaking about itself, not a caution about
	// something the reader did.
	return (
		<Box
			role="status"
			sx={{
				position: 'fixed',
				bottom: 0,
				left: 0,
				right: 0,
				// Above the app, below the widget it is describing — the launcher sits
				// at 2147483000-odd, and covering it would defeat the preview.
				zIndex: 2000,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				flexWrap: 'wrap',
				gap: { xs: 1, sm: 2 },
				py: 1.25,
				// Room on the right for the launcher, which sits in that corner.
				pl: 2,
				pr: { xs: 2, sm: 12 },
				color: 'common.white',
				backgroundColor: 'error.main',
				backgroundImage: (theme) =>
					`linear-gradient(90deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`,
				boxShadow: '0 -6px 24px rgba(0,0,0,0.28)'
			}}
		>
			<Box display="flex" alignItems="center" gap={1.25} sx={{ minWidth: 0 }}>
				<Box
					sx={{
						width: 26,
						height: 26,
						flexShrink: 0,
						borderRadius: '50%',
						display: 'grid',
						placeItems: 'center',
						// A held-back white rather than a second colour: the bar is already
						// the loudest thing on the page.
						backgroundColor: 'rgba(255,255,255,0.18)'
					}}
				>
					<VisibilityRoundedIcon sx={{ fontSize: 16, color: 'common.white' }} />
				</Box>
				<Box sx={{ minWidth: 0 }}>
					<Typography variant="body2" fontWeight={700} lineHeight={1.3}>
						Previewing Superchat Web Widget · v{previewVersion}
					</Typography>
					<Typography variant="caption" sx={{ opacity: 0.85, lineHeight: 1.3 }}>
						Only you can see this. It ignores the feature flag, so it shows even when the widget is off.
					</Typography>
				</Box>
			</Box>
			<PillButton
				size="small"
				startIcon={<CloseRoundedIcon />}
				onClick={exitPreview}
				sx={{
					flexShrink: 0,
					// The atom's contained treatment is navy on navy here, so the pill is
					// inverted: white on the red, which is the same relationship the
					// primary pill has with its own ground.
					'&:not(.Mui-disabled)': {
						color: 'error.main',
						backgroundColor: 'common.white',
						border: 'none',
						boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
						'&:hover': { backgroundColor: 'rgba(255,255,255,0.88)' }
					}
				}}
			>
				Exit preview
			</PillButton>
		</Box>
	);
};

export default SuperchatWidget;
